import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { Space } from '../../mainApi';

type SpaceThumbnailDialogProps = {
  open: boolean;
  space: Space | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
};

export const SpaceThumbnailDialog = ({
  open,
  space,
  isSaving,
  onClose,
  onSubmit,
}: SpaceThumbnailDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setError(null);
    }
  }, [open]);

  const previewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;

    if (!nextFile) {
      setSelectedFile(null);
      setError(null);
      return;
    }

    if (!nextFile.type.startsWith('image/')) {
      setSelectedFile(null);
      setError('Please choose an image file.');
      return;
    }

    setSelectedFile(nextFile);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Choose an image to continue.');
      return;
    }

    try {
      await onSubmit(selectedFile);
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : 'Unable to upload the thumbnail.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Set Thumbnail</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload an image for {space?.title || 'this space'}.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Button variant="outlined" component="label" disabled={isSaving}>
            Choose Image
            <input hidden type="file" accept="image/*" onChange={handleFileChange} />
          </Button>

          <Typography variant="body2" color={selectedFile ? 'text.primary' : 'text.secondary'}>
            {selectedFile ? selectedFile.name : 'No image selected'}
          </Typography>

          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Selected thumbnail preview"
              sx={{
                width: '100%',
                maxHeight: 280,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid #e6e8ef',
                bgcolor: '#f7f8fb',
                p: 1,
              }}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} disabled={isSaving || !selectedFile} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
